import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Form, Spinner} from "react-bootstrap";
import {monthKey, monthName, monthYear, shortDate} from "./rsoForecastWindowHelpers";
import {changedOnText, clearInputWindow, describeInputWindowFailure, inputWindowProblem, readInputWindow,
  saveInputWindow} from "./useRsoInputWindow";
import RsoSignInExpired from "./RsoSignInExpired";
import "./RsoInputWindowPanel.css";

// The body of the Admin page's "Set RSO input window" pop-up (RsoInputWindowButton), for accounts NANCY says may
// change it (the manage-rso-input-window permission, reported as canEdit).
// It opens on NANCY's current input month (the first read asks for no month) and offers the months NANCY lets an
// admin change. It shows that month's current window, then the Denver dates it opens and closes: Save sets them,
// "Use the standard rule" clears them. NANCY checks every rule and keeps the history.
// Each save or clear sends the change the admin was looking at (expectedLastChangeId): if someone else changed
// the month since, NANCY refuses (409), the panel says so and reloads the month.
// onBusyChange(busy): a save or clear is running, so the pop-up stays open until NANCY answers.
export default function RsoInputWindowPanel({token, onBusyChange}) {
  // request: the month asked for ("" = NANCY's current input month). loaded: NANCY's answer for the month on screen.
  const [request, setRequest] = useState("");
  const [loaded, setLoaded] = useState(null);
  const [months, setMonths] = useState([]);
  const [fields, setFields] = useState({opensOn: "", closesOn: ""});
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [signInExpired, setSignInExpired] = useState(false);
  const [notice, setNotice] = useState("");
  const busyRef = useRef(false);
  const alive = useRef(true);
  useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
  useEffect(() => {if (onBusyChange) onBusyChange(!!busy);}, [busy, onBusyChange]);

  useEffect(() => {
    if (!token) return undefined;
    let current = true;
    setLoaded(null);setLoadError("");setSignInExpired(false);
    readInputWindow(token, request).then((value) => {
      if (!current) return;
      setLoaded(value);setForbidden(!value.canEdit);
      setFields({opensOn: value.opensOn, closesOn: value.closesOn});
      if (value.editableMonths.length) setMonths(value.editableMonths);
    }).catch((failure) => {
      if (!current) return;
      const described = describeInputWindowFailure(failure, "The RSO input window couldn't be loaded.");
      setForbidden(described.forbidden);
      setSignInExpired(described.signInExpired);
      setLoadError(described.message || "The RSO input window couldn't be loaded.");
    });
    return () => {current = false;};
  }, [token, request, attempt]);

  if (forbidden) return <Alert variant="secondary" className="riw-message">{"Your account can't set the RSO input window."}</Alert>;

  const month = monthKey(loaded?.inputMonth) || request;
  const options = month && !months.includes(month) ? [...months, month].sort() : months;
  const apply = (value, message) => {
    setLoaded(value);setForbidden(!value.canEdit);
    setFields({opensOn: value.opensOn, closesOn: value.closesOn});
    if (value.editableMonths.length) setMonths(value.editableMonths);
    setNotice(message);
  };
  const run = async (kind, action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(kind);setError("");setNotice("");setSignInExpired(false);
    try {
      await action();
    } catch (failure) {
      if (!alive.current) return;
      const described = describeInputWindowFailure(failure, kind === "clear"
        ? "The standard rule couldn't be restored. Try again." : "The RSO input window couldn't be saved. Try again.");
      setSignInExpired(described.signInExpired);setError(described.message);
      // Someone else changed the month: show what is saved now (one re-read of this month).
      if (described.conflict) {
        if (request !== month) setRequest(month); else setAttempt((value) => value + 1);
      }
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy("");
    }
  };
  const save = (event) => {
    event.preventDefault();
    if (!loaded) return;
    const problem = inputWindowProblem(fields);
    if (problem) {setError(problem);setNotice("");return;}
    run("save", async () => {
      const value = await saveInputWindow(token, {inputMonth: month, ...fields, expectedLastChangeId: loaded.lastChangeId});
      if (alive.current) apply(value, `Saved. ${monthName(month)} entry is open ${shortDate(value.opensOn)} – ${shortDate(value.closesOn)}, Mountain Time.`);
    });
  };
  const clear = () => run("clear", async () => {
    const value = await clearInputWindow(token, month, loaded?.lastChangeId);
    if (alive.current) apply(value, `${monthName(month)} uses the standard rule again: ${shortDate(value.opensOn)} – ${shortDate(value.closesOn)}.`);
  });
  const changeMonth = (value) => {setRequest(value);setError("");setNotice("");};
  const disabled = !!busy || !loaded;
  const dirty = !!loaded && (fields.opensOn !== loaded.opensOn || fields.closesOn !== loaded.closesOn);

  const dateField = (name, label) => <Form.Group className="riw-field" controlId={`riw-${name}`}>
    <Form.Label>{label}</Form.Label>
    <Form.Control type="date" value={fields[name]} disabled={disabled}
      onChange={(event) => {setFields({...fields, [name]: event.target.value});setError("");setNotice("");}} />
  </Form.Group>;

  return <form className="riw" aria-label="Set RSO input window" onSubmit={save} noValidate>
    {options.length > 0 && <Form.Group className="riw-field" controlId="riw-month">
      <Form.Label>RSO input month</Form.Label>
      <Form.Select value={month} disabled={!!busy} onChange={(event) => changeMonth(event.target.value)}>
        {options.map((value) => <option key={value} value={value}>{monthYear(value)}</option>)}
      </Form.Select>
    </Form.Group>}
    {loaded && <div className="riw-current">
      <p className="riw-current-dates">{`Current ${monthName(month)} window: ${shortDate(loaded.opensOn)} – ${shortDate(loaded.closesOn)}`}</p>
      <p className="riw-current-source">{loaded.configured ? "Set by an admin." : "The standard rule: the last 7 days of the month before."}</p>
    </div>}
    {!loaded && !loadError && !signInExpired && <p className="riw-note">Loading the input window…</p>}
    {(loaded || options.length > 0) && <>
      {dateField("opensOn", "Window opens")}
      {dateField("closesOn", "Window closes")}
      <p className="riw-note">Mountain Time. Opens at 12:00 AM, closes at 11:59 PM.</p>
      <Button type="submit" className="riw-save" disabled={disabled || !dirty}>
        {busy === "save" ? <><Spinner as="span" animation="border" size="sm" aria-hidden="true" /> Saving…</> : "Save"}</Button>
      <Button type="button" variant="outline-secondary" className="riw-clear" disabled={disabled || !loaded?.configured}
        onClick={clear}>{busy === "clear" ? "Restoring…" : "Use the standard rule"}</Button>
    </>}
    {loaded?.lastChange && <p className="riw-note riw-changed">
      Last changed {loaded.lastChange.changedByName ? `by ${loaded.lastChange.changedByName} ` : ""}on {changedOnText(loaded.lastChange.changedOnUtc)}.</p>}
    {signInExpired ? <RsoSignInExpired className="riw-message" />
      : (error || (!loaded && loadError)) && <Alert variant="danger" className="riw-message" role="alert">{error || loadError}
        {!loaded && loadError && <>{" "}<Button variant="link" size="sm" className="p-0 align-baseline"
          onClick={() => setAttempt((value) => value + 1)}>Try again</Button></>}</Alert>}
    {notice && <p className="riw-notice" role="status">{notice}</p>}
  </form>;
}
